import React, { useState, useEffect } from 'react';
import { 
  X, Sparkles, Radio, Image, TrendingUp, Zap, Activity, 
  CheckCircle, ArrowRight, Music, Calendar, Palette, Star,
  FileText, Package
} from 'lucide-react';
import { useEscapeKey } from '../hooks/useEscapeKey';

interface WhatsNewProps {
  onClose: () => void;
}

const WhatsNew: React.FC<WhatsNewProps> = ({ onClose }) => {
  const [show, setShow] = useState(false);
  useEscapeKey(() => {
    setShow(false);
    setTimeout(onClose, 300);
  });

  useEffect(() => {
    // Animate in after mount
    setTimeout(() => setShow(true), 100);
  }, []);

  const handleClose = () => {
    setShow(false);
    setTimeout(onClose, 300); // Wait for animation
  };

  const newFeatures = [
    {
      icon: Image,
      title: '🎨 Daily AI Motivation',
      description: 'Start every day inspired! AI generates a new motivational image and quote daily to energize your sales team.',
      color: 'from-accent to-accent',
      highlight: true
    },
    {
      icon: Radio,
      title: '🎵 Sales Radio',
      description: 'Upload and play motivational music right in your dashboard and sidebar. Keep the team energized all day!',
      color: 'from-accent to-accent',
      highlight: true
    },
    {
      icon: FileText,
      title: '📄 Professional Offer Builder',
      description: 'Create stunning German-compliant offers & invoices (Angebote & Rechnungen) with AI-generated content, PDF export, and full VAT calculations.',
      color: 'from-success to-success0',
      highlight: true
    },
    {
      icon: Package,
      title: '📦 Marketing Material Hub',
      description: 'Upload PDFs, extract text with AI, and automatically generate LinkedIn posts and newsletter content from your use cases and case studies.',
      color: 'from-warning to-warning',
      highlight: true
    },
    {
      icon: Activity,
      title: '📊 Activity Feed & Stream',
      description: 'Stay connected! The activity feed is now embedded in the dashboard for quick team updates without navigation.',
      color: 'from-accent to-accent'
    },
    {
      icon: Sparkles,
      title: '🤖 5 AI Recommendations',
      description: 'Business Hub now shows 5 smart recommendations to help you prioritize your day with AI-powered insights.',
      color: 'from-accent to-accent'
    },
    {
      icon: TrendingUp,
      title: '✨ Unified Clean Design',
      description: 'Completely refreshed UI! All pages now follow the same modern, professional design with subtle shadows and smooth animations.',
      color: 'from-txt-secondary to-bg-raised'
    },
    {
      icon: Palette,
      title: '🎯 Enhanced Dashboard',
      description: 'Redesigned widgets with glassmorphism effects, better spacing, and equal-height components for a balanced look.',
      color: 'from-danger to-accent'
    }
  ];

  return (
    <div 
      className={`fixed inset-0 bg-black/60 backdrop-blur-sm z-[100] flex items-center justify-center p-4 transition-opacity duration-300 ${
        show ? 'opacity-100' : 'opacity-0'
      }`}
      onClick={handleClose}
    >
      <div 
        className={`bg-bg-card backdrop-blur-sm rounded-2xl shadow-heavy max-w-4xl w-full max-h-[90vh] overflow-hidden transform transition-all duration-300 ${
          show ? 'scale-100 opacity-100' : 'scale-95 opacity-0'
        }`}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header with Gradient */}
        <div className="bg-gradient-to-r from-accent bg-info-soft to-accent p-8 text-txt-inverted relative overflow-hidden">
          {/* Animated background elements */}
          <div className="absolute inset-0 opacity-20">
            <div className="absolute top-0 right-0 w-64 h-64 bg-bg-card rounded-full blur-3xl animate-pulse" />
            <div className="absolute bottom-0 left-0 w-64 h-64 bg-bg-card rounded-full blur-3xl animate-pulse" style={{ animationDelay: '1s' }} />
          </div>
          
          <button
            onClick={handleClose}
            className="absolute top-4 right-4 p-2 hover:bg-bg-card/20 rounded-lg transition-colors"
          >
            <X className="w-6 h-6" />
          </button>
          
          <div className="relative z-10">
            <div className="flex items-center space-x-3 mb-4">
              <div className="p-3 bg-bg-card/20 rounded-xl backdrop-blur-sm">
                <Sparkles className="w-8 h-8 animate-pulse" />
              </div>
              <div>
                <h2 className="text-3xl font-bold mb-1">What's New in Business Hub! 🎉</h2>
                <p className="text-accent">Exciting new features to supercharge your sales workflow</p>
              </div>
            </div>
            
            <div className="flex items-center space-x-2 mt-4">
              <Star className="w-5 h-5 text-warning" />
              <p className="text-sm text-txt-inverted/90">8 powerful new features just launched!</p>
            </div>
          </div>
        </div>

        {/* Features Grid */}
        <div className="p-8 overflow-y-auto max-h-[calc(90vh-200px)]">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {newFeatures.map((feature, index) => (
              <div
                key={index}
                className={`relative overflow-hidden rounded-xl p-6 transition-all duration-300 hover:scale-[1.02] ${
                  feature.highlight 
                    ? 'bg-gradient-to-br ' + feature.color + ' text-txt-inverted shadow-card' 
                    : 'bg-bg-surface hover:bg-bg-card border-2 border-default hover:border-accent hover:shadow-card'
                }`}
                style={{
                  animationDelay: `${index * 100}ms`
                }}
              >
                {/* Icon */}
                <div className={`inline-flex p-3 rounded-lg mb-4 ${
                  feature.highlight 
                    ? 'bg-bg-card/20 backdrop-blur-sm' 
                    : 'bg-gradient-to-br ' + feature.color
                }`}>
                  <feature.icon className={`w-6 h-6 ${feature.highlight ? 'text-txt-inverted' : 'text-txt-inverted'}`} />
                </div>

                {/* Badge for highlights */}
                {feature.highlight && (
                  <div className="absolute top-3 right-3">
                    <span className="px-2 py-1 bg-white/30 backdrop-blur-sm text-xs font-bold rounded-full">
                      NEW
                    </span>
                  </div>
                )}

                {/* Content */}
                <h3 className={`text-lg font-bold mb-2 ${
                  feature.highlight ? 'text-txt-inverted' : 'text-txt'
                }`}>
                  {feature.title}
                </h3>
                <p className={`text-sm leading-relaxed ${
                  feature.highlight ? 'text-txt-inverted/90' : 'text-txt-secondary'
                }`}>
                  {feature.description}
                </p>

                {/* Hover indicator */}
                <div className={`mt-4 flex items-center text-sm font-medium ${
                  feature.highlight ? 'text-txt-inverted/80' : 'text-accent'
                }`}>
                  <span>Try it now</span>
                  <ArrowRight className="w-4 h-4 ml-1 group-hover:translate-x-1 transition-transform" />
                </div>
              </div>
            ))}
          </div>

          {/* Additional Info */}
          <div className="mt-8 p-6 bg-gradient-to-r from-accent to-accent rounded-xl border-2 border-accent">
            <div className="flex items-start space-x-4">
              <div className="p-2 bg-accent rounded-lg flex-shrink-0">
                <Zap className="w-5 h-5 text-txt-inverted" />
              </div>
              <div>
                <h4 className="font-bold text-txt mb-2">🚀 More improvements:</h4>
                <ul className="space-y-1 text-sm text-txt-secondary">
                  <li className="flex items-center">
                    <CheckCircle className="w-4 h-4 mr-2 text-success flex-shrink-0" />
                    Glassmorphism effects across all pages
                  </li>
                  <li className="flex items-center">
                    <CheckCircle className="w-4 h-4 mr-2 text-success flex-shrink-0" />
                    Smooth hover animations and transitions
                  </li>
                  <li className="flex items-center">
                    <CheckCircle className="w-4 h-4 mr-2 text-success flex-shrink-0" />
                    Responsive design improvements
                  </li>
                  <li className="flex items-center">
                    <CheckCircle className="w-4 h-4 mr-2 text-success flex-shrink-0" />
                    Consistent color palette and spacing
                  </li>
                  <li className="flex items-center">
                    <CheckCircle className="w-4 h-4 mr-2 text-success flex-shrink-0" />
                    Better performance and loading states
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="p-6 border-t border-default bg-bg-surface">
          <div className="flex items-center justify-between">
            <p className="text-sm text-txt-secondary">
              Ready to explore? Click anywhere to get started! 🎯
            </p>
            <button
              onClick={handleClose}
              className="px-6 py-3 bg-gradient-to-r from-accent to-info text-txt-inverted rounded-xl hover:from-accent hover:to-info font-semibold shadow-card hover:shadow-card transition-all hover:scale-105"
            >
              Let's Go! 🚀
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default WhatsNew;
